import React from 'react';
import { TagInput } from '@containers/form/TagInput';
import { TrackingEvent } from '@classes/TrackingEvent';
import { Container } from '@ui';
import { showErrorBanner } from '@utils/application';
import { useDispatch } from 'react-redux';
import { fetchCompanyDocsByFilters, fetchCompanyTagDocsByFilters, filterIsRecentDocs } from '@utils/doc';
import { Working } from '@ui/Working';
import { filterDuplicateCompanies } from '@utils/usefulFuncs';

export const TickerSelect = ({
  tickers,
  onAddTicker,
  onRemoveTicker,
  multipleTickersEnabled,
  isResearchMode,
  hideLabel,
}) => {
  const onAddTag = (t, n) => {
    TrackingEvent.create('Added Tickers For Digital Analyst', {
      Tickers: [t],
    });

    onAddTicker(t, n);
  };

  return (
    <Container>
      <TagInput
        label={multipleTickersEnabled ? 'Tickers' : 'Ticker'}
        tags={tickers}
        onAddTag={onAddTag}
        onRemoveTag={onRemoveTicker}
        isResearchMode={isResearchMode}
        hideLabel={hideLabel}
      />
    </Container>
  );
};

export const TickerDocGenerator = ({
  tickersRef,
  preSelectedTickers,
  onSelectDocs,
  onRemoveTicker,
  hideLabel,
  isQueryMode,
  isFedMode,
  isFedGpt,
  showCompanyTags,
}) => {
  const dispatch = useDispatch();
  const [tickers, _setTickers] = React.useState(preSelectedTickers || []);
  const [isLoading, setIsLoading] = React.useState(false);

  const autoSelectDocsForTicker = async (company) => {
    const isStock = !!company.company_id;
    const extraParams = {
      isQueryMode,
      isFedMode,
      isFedGpt,
    };
    setIsLoading(true);
    let docs;
    if (isStock) {
      docs = await fetchCompanyDocsByFilters({ companyIds: [company.company_id], ...extraParams });
    } else {
      docs = await fetchCompanyTagDocsByFilters({ companyTagIds: [company.doc_tag_id], ...extraParams });
    }
    setIsLoading(false);
    if (docs) {
      const filteredDocs = filterIsRecentDocs(docs, false);
      onSelectDocs(filteredDocs);
    } else {
      showErrorBanner('Something went wrong getting documents for ticker, please try again.', null, dispatch);
      handleRemoveTicker(company);
    }
  };

  const addTicker = (t) => {
    if (!t) return;

    _setTickers((prev) => {
      const nextTickers = filterDuplicateCompanies([...prev, t]);
      if (tickersRef) {
        tickersRef.current = nextTickers;
      }
      return nextTickers;
    });
    autoSelectDocsForTicker(t);
  };

  const handleRemoveTicker = (ticker) => {
    onRemoveTicker(ticker);
    const nextTickers = tickers.filter((t) => t !== ticker);
    if (tickersRef) {
      tickersRef.current = nextTickers;
    }
    _setTickers(nextTickers);
  };

  const onAddTag = (t, n, v) => {
    const eventProperties = v.doc_tag_id
      ? {
          'Private Company': v.name,
        }
      : {
          Ticker: [v.symbol],
        };
    TrackingEvent.create('Added Tickers For Digital Analyst', eventProperties);

    if (tickers.length >= 3) {
      showErrorBanner('A maximum of 3 companies is supported at this time', null, dispatch);
      return;
    }

    if (!tickers.some((t) => (t.doc_tag_id ? t.doc_tag_id === v.doc_tag_id : t.symbol === v.symbol))) {
      addTicker(v);
    }
  };

  return (
    <Container>
      <TagInput
        label={'Companies / Tags'}
        tags={tickers}
        onAddTag={onAddTag}
        onRemoveTag={handleRemoveTicker}
        hideLabel={hideLabel}
        showCompanyTags={showCompanyTags}
        includeInfoIcon
      />
      {isLoading && (
        <Container top={16}>
          <Working message={'Auto-selecting recent documents...'} noVerticalPadding />
        </Container>
      )}
    </Container>
  );
};
